<script setup lang="ts">
import {ConsumerReconciliation, ReconciliationCandidate} from "@/core/interfaces/consumers/ConsumerReconciliation";
import formatHelper from "@/core/helpers/formatHelper";
import dateHelper from "@/core/helpers/dateHelper";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Checkbox} from "@/core/components/ui/checkbox";
import {Link, Unlink} from "@lucide/vue";
import {ref} from "vue";
import {reconciliationApi} from "@/container";

const props = defineProps<{
    candidate: ReconciliationCandidate
}>();

const emit = defineEmits(['hide']);

/**
 * The ID of the reconciliation, if it exists.
 */
const reconciliation = ref<ConsumerReconciliation | null>(props.candidate?.reconciliation ?? null);

/**
 * The selected consumers in the candidates.
 */
const selectedConsumers = ref<string[]>(props.candidate.consumers?.map(consumer => consumer.key));
const loading = ref<'add' | 'reject' | null>(null);
/**
 * Toggles the selection of a consumer in the candidate.
 * @param selected  Whether the consumer is selected or not.
 * @param consumerKey  The key of the consumer to toggle.
 */
const toggleSelection = (selected: boolean, consumerKey: string) => {
    if (selected) {
        selectedConsumers.value.push(consumerKey);
    } else {
        selectedConsumers.value = selectedConsumers.value.filter(key => key !== consumerKey);
    }
};

/**
 * Updates the selected consumers based on the action, in a single batched
 * call: selected keys get the action, unselected keys get the opposite.
 * @param action  The action to perform: 'add' or 'reject'.
 */
const update = async (action: 'add' | 'reject') => {
    loading.value = action;

    const keys = [
        ...selectedConsumers.value.map(consumerKey => ({
            consumer_key: consumerKey,
            action
        })),
        ...props.candidate.consumer_keys
            .filter(key => !selectedConsumers.value.includes(key))
            .map(consumerKey => ({
                consumer_key: consumerKey,
                action: action === 'add' ? 'reject' as const : 'add' as const
            }))
    ];

    reconciliation.value = reconciliation.value
        ? await reconciliationApi().update(reconciliation.value.id, {keys})
        : await reconciliationApi().create({keys});

    emit('hide');
    loading.value = null;
};

/**
 * Checks if the reconciliation has been validated for a specific consumer key.
 * @param consumerKey The key of the consumer to check.
 */
const hasValidatedReconciliation = (consumerKey: string) => {
    return !!reconciliation.value?.keys.find(key => key.consumer_key === consumerKey && !!key.validated_at);
};

/**
 * Checks if the reconciliation has been rejected for a specific consumer key.
 * @param consumerKey   The key of the consumer to check.
 */
const hasRejectedReconciliation = (consumerKey: string) => {
    return !!reconciliation.value?.keys.find(key => key.consumer_key === consumerKey && !!key.rejected_at);
};
</script>

<template>
  <div class="border-b border-slate-50 py-2">
    <div class="font-bold">
      {{ formatHelper.patientName(candidate) }}
    </div>
    <div class="flex">
      <div class="flex flex-1 flex-col">
        <div v-for="(consumer, index) in candidate.consumers" :key="index" class="flex text-sm gap-2 items-center py-1">
          <div class="flex flex-1 items-center">
            <div class="px-4">
              <Checkbox
                :id="`${consumer.key}`"
                :model-value="selectedConsumers.includes(consumer.key)"
                @update:model-value="(selected) => toggleSelection(selected as boolean, consumer.key)"
              />
            </div>
            <div class="pr-4">
              <div class="flex items-center gap-1">
                {{ formatHelper.patientName(consumer) }},
                <span class="text-slate-500">
                  né le {{ dateHelper.shortFormat(consumer.birth_date) }}
                </span>
                <Link v-if="hasValidatedReconciliation(consumer.key)" class="w-4 mb-1 ml-1 text-success" />
                <Unlink v-if="hasRejectedReconciliation(consumer.key)" class="w-4 mb-1 ml-1 text-red-400" />
              </div>
              <div v-if="consumer.customer" class="text-primary">
                ayant droit de <strong>{{ formatHelper.patientName(consumer.customer) }}</strong>
                (N°SS {{ consumer.customer.nir }})
              </div>
              <div v-else class="text-primary">
                assuré, <strong>N°SS {{ consumer.nir }}</strong>
              </div>
              <div v-if="consumer.sales?.visits" class="text-slate-500 text-ellipsis line-clamp-1">
                {{ consumer.sales.visits || 0 }} visites |
                <span v-if="consumer.sales.last_visit">
                  dernière visite le {{ dateHelper.shortFormat(consumer.sales.last_visit) }} ventes pour
                  {{ consumer.sales.top_sales[0]?.item?.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col justify-center gap-4">
        <BaseButton density="compact" primary :loading="loading=='add'" @click="update('add')">
          <Link class="w-4 mr-1" />
          C'est le même patient
        </BaseButton>
        <BaseButton density="compact" :loading="loading=='reject'" @click="update('reject')">
          <Unlink class="w-4 mr-1" />
          C'est un patient différent
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
